import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { DATA_SOURCE_ROUTES, DATA_SOURCES } from 'constants/appDataSources';

const useAppDataSource = () => {
  const location = useLocation();

  const appDataSourceState = useSelector((state) => state?.appDataSource) || {};

  /**
   Does current route support app data source other than default?
   Implemented indicates if current page supports non-default data sources
   Disabled indicates if the data source SELECTION COMPONENT is disabled for the current page
   */
  const { appDataSourceImplemented, appDataSourceDisabled } = useMemo(
    () =>
      DATA_SOURCE_ROUTES.reduce(
        (result, route) => {
          if (route.path.test(location.pathname)) {
            result.appDataSourceImplemented = true;
            if (route.disabled) {
              result.appDataSourceDisabled = true;
            }
          }
          return result;
        },
        { appDataSourceImplemented: false, appDataSourceDisabled: false }
      ),
    [location.pathname]
  );

  /**
   * The user's selected app data source ID (may not be respected in certain contexts i.e. if page doesn't support all or non-default data sources)
   */
  const appDataSourceIdUserSelection = appDataSourceState?.selectedId ?? DATA_SOURCES.default.id;

  /**
   * The app data source that should be used, will be user selection if page supports non-default data sources, otherwise defaults to 'default'
   */
  const appDataSourceId = appDataSourceImplemented && appDataSourceIdUserSelection ? appDataSourceIdUserSelection : DATA_SOURCES.default.id;

  /**
   * Other info about the used app data source, besides the id
   */
  const dataSource = appDataSourceState?.dataSources?.[appDataSourceId] ?? DATA_SOURCES.default;
  const appDataSourceUrl = dataSource.url;
  const appDataSourceWebsite = dataSource.website;

  /**
   * Flags
   */
  const isAppDataSourceRemote = Boolean(appDataSourceId && appDataSourceId !== DATA_SOURCES.default.id);

  /**
   * Selector helpers
   */
  const useGetAppDataSources = () => useSelector((state) => state?.appDataSource?.dataSources);

  const useGetRemoteAppConfig = () => useSelector((state) => state?.appDataSource?.appConfig);

  return {
    /** Raw state (escape hatch, avoid using if possible) */
    appDataSource: appDataSourceState,

    /** Core values */
    appDataSourceId,
    appDataSourceIdUserSelection,
    appDataSourceImplemented,
    appDataSourceDisabled: appDataSourceDisabled || Boolean(appDataSourceState.disabled),

    /** Derived metadata */
    appDataSourceUrl,
    appDataSourceWebsite,
    isAppDataSourceRemote,

    /** Selectors */
    useGetAppDataSources,
    useGetRemoteAppConfig
  };
};

export default useAppDataSource;
